import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import i18n from "../../../i18n"
import {
    Avatar,
    CircularProgress,
    Dialog,
    DialogContent,
    Fab,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {Bot, Edit2, Menu, MessageSquare, Plus, Send, Trash2, User, X} from 'lucide-react';
import {tutorsApi} from '../../../services/api';
import {useError} from '../../../contexts/ErrorContext';

const ChatDialog = ({ open, onClose, tutor, token }) => {
    const { t } = useTranslation();
    const { showError } = useError();
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Состояния для бесед
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [showSidebar, setShowSidebar] = useState(true);
    const [editingConversation, setEditingConversation] = useState(null);
    const [newConversationName, setNewConversationName] = useState('');

    // Состояния чата
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [conversationsLoading, setConversationsLoading] = useState(false);

    // Загрузка бесед при открытии диалога
    useEffect(() => {
        if (open && token && tutor?.id) {
            fetchConversations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, token, tutor?.id]);

    // Загрузка истории при смене беседы
    useEffect(() => {
        if (currentConversation) {
            loadConversationHistory(currentConversation.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentConversation]);

    // Скролл к последнему сообщению
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Фокус на поле ввода
    useEffect(() => {
        if (open && inputRef.current && currentConversation) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [open, currentConversation]);

    // Загрузка бесед для конкретного AI-тьютора
    const fetchConversations = async () => {
        setConversationsLoading(true);
        try {
            const response = await tutorsApi.getConversations(token);
            const allConversations = response || [];
            const conversationsData = tutor?.id ? allConversations.filter(c => c.ai_tutor === tutor.id) : allConversations;

            setConversations(conversationsData || []);

            // Если есть беседы и не выбрана текущая, выбираем первую
            if (conversationsData && conversationsData.length > 0 && !currentConversation) {
                setCurrentConversation(conversationsData[0]);
            }
        } catch (err) {
            
            setConversations([]);
            showError(t('dashboard.ai_tutors.error.loading_conversations'));
        } finally {
            setConversationsLoading(false);
        }
    };

    // Загрузка истории беседы
    const loadConversationHistory = async (conversationId) => {
        setLoading(true);
        try {
            const response = await tutorsApi.getChatHistory(conversationId, token);
            const history = response || [];
            setMessages(history || []);
        } catch (err) {
            
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    // Создание новой беседы для конкретного AI-тьютора
    const createNewConversation = async () => {
        try {
            const conversationName = `${t('dashboard.ai_tutors.chat_with')} ${tutor.name} - ${new Date().toLocaleDateString()}`;

            const newConversation = await tutorsApi.createConversation(conversationName, tutor.id, token);

            if (!newConversation || newConversation.ai_tutor !== tutor.id) {
                showError(t('dashboard.ai_tutors.error.conversation_mismatch'));
                return;
            }

            setConversations(prev => [newConversation, ...prev]);
            setCurrentConversation(newConversation);
            setMessages([]); // ✅ ДОБАВЛЕНО: Очищаем сообщения для новой беседы
        } catch (err) {
            
            showError(t('dashboard.ai_tutors.error.creating_conversation'));
        }
    };

    // Переименование беседы через API функцию
    const updateConversationName = async (id, name) => {
        try {
            await tutorsApi.updateConversation(id, { name }, token);

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === id ? { ...conv, name } : conv
                )
            );

            if (currentConversation?.id === id) {
                setCurrentConversation(prev => ({ ...prev, name }));
            }

            setEditingConversation(null);
        } catch (err) {
            
            showError(t('dashboard.ai_tutors.error.updating_conversation_name'));
        }
    };

    // Архивирование беседы через API функцию
    const archiveConversation = async (id) => {
        if (!window.confirm(t('dashboard.ai_tutors.confirm_delete_conversation'))) {
            return;
        }

        try {
            await tutorsApi.archiveConversation(id, token);

            const updatedConversations = conversations.filter(conv => conv.id !== id);
            setConversations(updatedConversations);

            if (currentConversation?.id === id) {
                setCurrentConversation(updatedConversations.length > 0 ? updatedConversations[0] : null);
                setMessages([]);
            }

        } catch (err) {
            
            showError(t('dashboard.ai_tutors.error.deleting_conversation'));
        }
    };

    // Функция отправки сообщения
    const handleSendMessage = async () => {
        if (!message.trim() || !currentConversation || sendingMessage) return;

        if (!currentConversation.id || currentConversation.ai_tutor !== tutor.id) {
            showError(t('dashboard.ai_tutors.error.invalid_conversation'));
            return;
        }

        setSendingMessage(true);
        const messageText = message;
        setMessage(''); // Очищаем поле сразу

        try {
            // Добавляем сообщение пользователя
            const userMessage = {
                id: Date.now(),
                role: 'user',
                content: messageText,
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, userMessage]);

            // Получаем текущий язык динамически
            const currentLanguage = i18n.language;

            // Отправляем сообщение тьютору
            const response = await tutorsApi.sendMessage(tutor.id, {
                question: messageText,
                conversation_id: currentConversation.id,
                language: currentLanguage
            }, token);

            // Добавляем ответ тьютора
            const data = response || {};
            const assistantMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: data.response || data.answer || '',
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            
            showError(t('dashboard.ai_tutors.error.sending_message'));
        } finally {
            setSendingMessage(false);
        }
    };

    // Обработчик нажатия Enter
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Форматирование времени
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Начать редактирование беседы
    const startEditingConversation = (conversation) => {
        setEditingConversation(conversation.id);
        setNewConversationName(conversation.name);
    };

    // Обработка формы редактирования
    const handleEditFormSubmit = (e, id) => {
        e.preventDefault();
        if (newConversationName.trim()) {
            updateConversationName(id, newConversationName.trim());
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '20px',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Заголовок чата */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowSidebar(!showSidebar)}
                        className="p-2 hover:bg-white/20 rounded-full md:hidden"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <Avatar
                        sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: 'rgba(255,255,255,0.2)'
                        }}
                    >
                        <Bot className="w-6 h-6" />
                    </Avatar>
                    <div>
                        <Typography variant="h6" className="font-medium">
                            {tutor.name}
                        </Typography>
                        <Typography variant="caption" className="opacity-75">
                            {tutor.instruction_type === 'feedback'
                                ? t('dashboard.ai_tutors.type.feedback')
                                : t('dashboard.ai_tutors.type.assistant')
                            }
                        </Typography>
                    </div>
                </div>
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'white' }}
                >
                    <X className="w-6 h-6" />
                </IconButton>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Боковая панель с беседами */}
                {showSidebar && (
                    <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-purple-50">
                            <div>
                                <Typography variant="h6" className="font-semibold text-gray-800">
                                    {t('dashboard.ai_tutors.conversations')}
                                </Typography>
                                <Typography variant="caption" className="text-purple-600">
                                    {tutor.name}
                                </Typography>
                            </div>
                            <Tooltip title={t('dashboard.ai_tutors.new_conversation')}>
                                <IconButton
                                    onClick={createNewConversation}
                                    size="small"
                                    className="text-purple-600 hover:bg-purple-100"
                                >
                                    <Plus className="w-5 h-5" />
                                </IconButton>
                            </Tooltip>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {conversationsLoading ? (
                                <div className="p-4 text-center">
                                    <CircularProgress size={24} className="text-purple-500" />
                                </div>
                            ) : conversations.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    <Typography variant="body2">
                                        {t('dashboard.ai_tutors.no_conversations')}
                                    </Typography>
                                    <button
                                        onClick={createNewConversation}
                                        className="mt-2 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-md text-sm transition-colors"
                                    >
                                        {t('dashboard.ai_tutors.create_first_conversation')}
                                    </button>
                                </div>
                            ) : (
                                conversations.map((conversation) => (
                                    <div key={conversation.id} className="border-b border-gray-100 last:border-b-0">
                                        {editingConversation === conversation.id ? (
                                            <form onSubmit={(e) => handleEditFormSubmit(e, conversation.id)} className="p-3">
                                                <TextField
                                                    value={newConversationName}
                                                    onChange={(e) => setNewConversationName(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    autoFocus
                                                    variant="outlined"
                                                />
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        type="submit"
                                                        className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
                                                    >
                                                        OK
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingConversation(null)}
                                                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                </div>
                                            </form>
                                        ) : (
                                            <div
                                                className={`group p-3 flex items-center justify-between cursor-pointer transition-colors ${
                                                    currentConversation?.id === conversation.id
                                                        ? 'bg-purple-100 border-l-4 border-purple-500'
                                                        : 'hover:bg-gray-100'
                                                }`}
                                                onClick={() => setCurrentConversation(conversation)}
                                            >
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    <Typography
                                                        variant="body2"
                                                        className="truncate text-gray-800"
                                                    >
                                                        {conversation.name}
                                                    </Typography>
                                                </div>

                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditingConversation(conversation);
                                                        }}
                                                        className="text-gray-400 hover:text-gray-600"
                                                        title={t('dashboard.ai_tutors.rename_conversation')}
                                                    >
                                                        <Edit2 className="w-3 h-3" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            archiveConversation(conversation.id);
                                                        }}
                                                        className="text-gray-400 hover:text-red-500"
                                                        title={t('dashboard.ai_tutors.delete_conversation')}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Область сообщений */}
                <DialogContent
                    className="flex-grow p-0 overflow-hidden bg-gray-50"
                    sx={{ display: 'flex', flexDirection: 'column' }}
                >
                    <div className="flex-grow overflow-y-auto p-4 space-y-4">
                        {loading ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center">
                                    <CircularProgress className="text-purple-500" />
                                    <Typography variant="body2" className="mt-2 text-gray-500">
                                        {t('dashboard.ai_tutors.chat_dialog.loading_history')}
                                    </Typography>
                                </div>
                            </div>
                        ) : !currentConversation ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <div className="bg-white rounded-full p-6 shadow-sm mb-4">
                                    <MessageSquare className="w-12 h-12 text-purple-300" />
                                </div>
                                <Typography variant="h6" className="mb-2">
                                    {t('dashboard.ai_tutors.select_conversation')}
                                </Typography>
                                <button
                                    onClick={createNewConversation}
                                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-md transition-colors"
                                >
                                    {t('dashboard.ai_tutors.new_conversation')}
                                </button>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                <div className="bg-white rounded-full p-6 shadow-sm mb-4">
                                    <MessageSquare className="w-12 h-12 text-purple-300" />
                                </div>
                                <Typography variant="h6" className="mb-2">
                                    {t('dashboard.ai_tutors.chat_dialog.welcome')}
                                </Typography>
                                <Typography variant="body2" className="text-center max-w-sm">
                                    {t('dashboard.ai_tutors.chat_dialog.start_conversation')}
                                </Typography>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[75%] ${
                                                msg.role === 'user'
                                                    ? 'order-2'
                                                    : 'order-1'
                                            }`}
                                        >
                                            <div className={`flex items-end gap-2 ${
                                                msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                                            }`}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        backgroundColor: msg.role === 'user'
                                                            ? '#3b82f6'
                                                            : '#8b5cf6'
                                                    }}
                                                >
                                                    {msg.role === 'user'
                                                        ? <User className="w-4 h-4" />
                                                        : <Bot className="w-4 h-4" />
                                                    }
                                                </Avatar>

                                                <div className={`rounded-2xl px-4 py-3 max-w-full ${
                                                    msg.role === 'user'
                                                        ? 'bg-blue-500 text-white rounded-br-md'
                                                        : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                                                }`}>
                                                    <div
                                                        className="break-words"
                                                        dangerouslySetInnerHTML={{
                                                            __html: msg.content.replace(/\n/g, '<br>')
                                                        }}
                                                    />
                                                    <div className={`text-xs mt-1 ${
                                                        msg.role === 'user'
                                                            ? 'text-blue-100'
                                                            : 'text-gray-400'
                                                    }`}>
                                                        {formatTime(msg.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Индикатор печати */}
                                {sendingMessage && (
                                    <div className="flex justify-start">
                                        <div className="flex items-end gap-2">
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    backgroundColor: '#8b5cf6'
                                                }}
                                            >
                                                <Bot className="w-4 h-4" />
                                            </Avatar>
                                            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    {/* Поле ввода */}
                    {currentConversation && (
                        <div className="p-4 bg-white border-t border-gray-200">
                            <div className="flex items-end gap-3">
                                <TextField
                                    ref={inputRef}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder={t('dashboard.ai_tutors.chat_dialog.message_placeholder')}
                                    multiline
                                    maxRows={4}
                                    fullWidth
                                    variant="outlined"
                                    disabled={sendingMessage}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '20px',
                                            backgroundColor: '#f8f9fa'
                                        }
                                    }}
                                />
                                <Tooltip title={t('dashboard.ai_tutors.chat_dialog.send')}>
                                    <span>
                                        <Fab
                                            size="small"
                                            onClick={handleSendMessage}
                                            disabled={!message.trim() || sendingMessage}
                                            sx={{
                                                background: 'linear-gradient(45deg, #8b5cf6, #a855f7)',
                                                color: 'white',
                                                '&:hover': {
                                                    background: 'linear-gradient(45deg, #7c3aed, #9333ea)',
                                                },
                                                '&.Mui-disabled': {
                                                    backgroundColor: '#e5e7eb',
                                                    color: '#9ca3af'
                                                }
                                            }}
                                        >
                                            {sendingMessage ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                <Send className="w-4 h-4" />
                                            )}
                                        </Fab>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </div>
        </Dialog>
    );
};

export default ChatDialog;